<template>
  <div class="inv-page">
    <BackButton />
    <div v-if="loading" class="inv-loading"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
    <div v-else-if="!invoice" class="inv-loading">Invoice not found.</div>
    <template v-else>
      <div class="inv-top">
        <div>
          <h2>{{ invoice.invoice_number }}</h2>
          <div class="inv-meta">
            <span class="inv-status" :class="'st-'+invoice.status">{{ statusLabel(invoice.status) }}</span>
            <span>Issued {{ invoice.issue_date }} · Due {{ invoice.due_date }}</span>
          </div>
        </div>
        <div class="inv-actions">
          <button class="inv-btn-2nd" @click="copyShareLink()"><i class="mdi mdi-link-variant"></i> Copy share link</button>
          <button class="inv-btn-2nd" @click="openPdf()"><i class="mdi mdi-file-pdf-box"></i> View PDF</button>
          <button v-if="canRecordPayment" class="inv-btn" @click="paymentModal=true"><i class="mdi mdi-cash"></i> Record payment</button>
          <button v-if="canCancel" class="inv-btn-danger" @click="cancel()"><i class="mdi mdi-close-circle"></i> Cancel invoice</button>
        </div>
      </div>

      <div class="inv-grid">
        <div class="inv-block">
          <h4>Bill To</h4>
          <div class="inv-block-body">
            <strong>{{ invoice.company_name }}</strong><br>
            <span v-if="invoice.contact_email">{{ invoice.contact_email }}<br></span>
            <span v-if="invoice.contact_phone">{{ invoice.contact_phone }}<br></span>
            <span v-if="invoice.buyer_kra">KRA PIN: {{ invoice.buyer_kra }}</span>
          </div>
        </div>
        <div class="inv-block">
          <h4>Totals</h4>
          <div class="inv-block-body">
            <div class="row"><span>Subtotal</span><span>{{ fmtMoney(invoice.subtotal_kes) }}</span></div>
            <div v-if="Number(invoice.vat_kes)>0" class="row"><span>VAT</span><span>{{ fmtMoney(invoice.vat_kes) }}</span></div>
            <div v-if="Number(invoice.wht_kes)>0" class="row"><span>WHT</span><span>−{{ fmtMoney(invoice.wht_kes) }}</span></div>
            <div class="row total"><span>Total {{ invoice.currency }}</span><span>{{ fmtMoney(invoice.total_kes) }}</span></div>
            <div class="row paid"><span>Paid</span><span>−{{ fmtMoney(invoice.paid_kes) }}</span></div>
            <div class="row balance"><span>Balance Due</span><span>{{ fmtMoney(invoice.balance_kes) }}</span></div>
          </div>
        </div>
      </div>

      <h3 class="inv-h3">Line Items</h3>
      <div class="table-responsive"><table class="inv-table">
        <thead><tr><th>#</th><th>Ear Tag</th><th>Slaughter Date</th><th>Grade</th><th class="num">Weight ({{ invoice.billing_weight_basis }})</th><th class="num">Rate/kg</th><th class="num">Amount</th></tr></thead>
        <tbody>
          <tr v-for="(ln, i) in lines" :key="ln.id">
            <td>{{ i+1 }}</td>
            <td>{{ ln.ear_tag || ln.description || "—" }}</td>
            <td>{{ ln.slaughter_date || "—" }}</td>
            <td>{{ ln.grade || "—" }}</td>
            <td class="num">{{ Number(ln.weight_kg).toFixed(2) }} kg <span v-if="Number(ln.condemned_weight_kg)>0" class="cond-note" :title="'Net of '+Number(ln.condemned_weight_kg).toFixed(2)+' kg condemned'">*</span></td>
            <td class="num">{{ fmtMoney(ln.rate_per_kg) }}</td>
            <td class="num"><strong>{{ fmtMoney(ln.amount_kes) }}</strong></td>
          </tr>
        </tbody>
      </table></div>

      <div v-if="payments.length" class="inv-payments">
        <h3 class="inv-h3">Payments Received</h3>
        <div class="table-responsive"><table class="inv-table">
          <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th class="num">Amount</th></tr></thead>
          <tbody>
            <tr v-for="p in payments" :key="p.id">
              <td>{{ p.payment_date }}</td>
              <td>{{ formatMethod(p.payment_method) }}</td>
              <td>{{ p.payment_ref || "—" }}</td>
              <td class="num"><strong>{{ fmtMoney(p.amount_kes) }}</strong></td>
            </tr>
          </tbody>
        </table></div>
      </div>

      <!-- RECORD PAYMENT MODAL -->
      <div v-if="paymentModal" class="modal-bg" @click.self="paymentModal=false">
        <div class="modal">
          <h3>Record Payment</h3>
          <p class="muted">Balance due: <strong>{{ fmtMoney(invoice.balance_kes) }}</strong></p>
          <div class="row-2">
            <div class="field"><label>Amount (KES)</label><input v-model.number="pay.amount" type="number" min="0"></div>
            <div class="field"><label>Payment date</label><input v-model="pay.paymentDate" type="date"></div>
          </div>
          <div class="row-2">
            <div class="field"><label>Method</label>
              <select v-model="pay.paymentMethod">
                <option value="rtgs">RTGS</option>
                <option value="bank_transfer">Bank transfer</option>
                <option value="mpesa_paybill">M-Pesa paybill</option>
                <option value="mpesa_stk">M-Pesa STK ref</option>
                <option value="cheque">Cheque</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="field"><label>Reference</label><input v-model="pay.paymentRef" placeholder="Bank ref / cheque no / M-Pesa code"></div>
          </div>
          <div class="field"><label>Bulk payment ref (optional — same code if this is one of many invoices settled by a single transfer)</label><input v-model="pay.bulkPaymentRef"></div>
          <div class="modal-foot">
            <button class="inv-btn-2nd" @click="paymentModal=false">Cancel</button>
            <button class="inv-btn" :disabled="savingPay" @click="recordPayment()"><span v-if="savingPay"><i class="mdi mdi-loading mdi-spin"></i></span><span v-else>Record</span></button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { executeJson, flocker } from "@/api";
import BackButton from "@/components/BackButton.vue";
export default {
  name: "InvoiceDetailPage",
  components: { BackButton },
  data(){return{
    loading: true,
    invoice: null,
    lines: [],
    payments: [],
    paymentModal: false,
    savingPay: false,
    pay: { amount: 0, paymentDate: new Date().toISOString().slice(0,10), paymentMethod: "rtgs", paymentRef: "", bulkPaymentRef: "" },
  }},
  computed:{
    invoiceId(){ return this.$route.params.id; },
    canRecordPayment(){ return this.invoice && !["paid","cancelled","draft"].includes(this.invoice.status); },
    canCancel(){ return this.invoice && Number(this.invoice.paid_kes) === 0 && this.invoice.status !== "cancelled"; },
  },
  mounted(){ this.load(); },
  methods:{
    async load(){
      this.loading = true;
      try {
        const r = await executeJson({ function: "getCorporateInvoice", invoiceId: this.invoiceId, userId: sessionStorage.getItem("userId") }, flocker);
        if (r.data && r.data.success) {
          this.invoice = r.data.data.invoice;
          this.lines = r.data.data.lines || [];
          this.payments = r.data.data.payments || [];
          this.pay.amount = Number(this.invoice.balance_kes);
        }
      } catch (e){ /* silent */ }
      this.loading = false;
    },
    async recordPayment(){
      this.savingPay = true;
      try {
        const r = await executeJson({ function: "recordCorporateInvoicePayment", invoiceId: this.invoiceId, userId: sessionStorage.getItem("userId"), ...this.pay }, flocker);
        if (r.data && r.data.success) { this.paymentModal = false; await this.load(); }
        else alert((r.data && r.data.message) || "Record failed");
      } catch (e){ alert("Record failed: " + e.message); }
      this.savingPay = false;
    },
    async cancel(){
      const reason = prompt("Reason for cancellation? (optional)");
      if (reason === null) return;
      try {
        const r = await executeJson({ function: "cancelCorporateInvoice", invoiceId: this.invoiceId, userId: sessionStorage.getItem("userId"), reason: reason || "" }, flocker);
        if (r.data && r.data.success) await this.load();
        else alert((r.data && r.data.message) || "Cancel failed");
      } catch (e){ /* silent */ }
    },
    copyShareLink(){
      const base = window.location.origin + window.location.pathname;
      const link = base + "#/invoice/" + this.invoice.public_token;
      navigator.clipboard.writeText(link).then(() => alert("Link copied: " + link));
    },
    openPdf(){
      if (this.invoice.pdf_path) window.open((process.env.VUE_APP_API_BASE || "") + "/" + this.invoice.pdf_path, "_blank");
      else alert("PDF not yet generated");
    },
    fmtMoney(v){ return Number(v || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}); },
    formatMethod(m){ return (m || "").replace(/_/g, " "); },
    statusLabel(s){ return ({ sent:"Sent", viewed:"Viewed", partially_paid:"Partially paid", paid:"Paid", overdue:"Overdue", draft:"Draft", cancelled:"Cancelled" })[s] || s; },
  }
};
</script>

<style scoped>
.inv-page{max-width:1100px;margin:0 auto;padding:24px 20px 80px;color:#1A1A1A}
html[data-theme="dark"] .inv-page{color:#F5F1E8}
.inv-loading{text-align:center;padding:60px;color:#8A827A}
.inv-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;gap:14px;flex-wrap:wrap}
.inv-top h2{margin:0;font-size:24px;letter-spacing:-0.3px}
.inv-meta{margin-top:4px;font-size:13px;color:#6B6356;display:flex;align-items:center;gap:10px}
.inv-actions{display:flex;gap:8px;flex-wrap:wrap}
.inv-btn,.inv-btn-2nd,.inv-btn-danger{padding:8px 14px;border-radius:7px;border:none;cursor:pointer;font-weight:600;font-size:13px;display:inline-flex;align-items:center;gap:5px}
.inv-btn{background:#BFA164;color:#1A1A1A}
.inv-btn:hover{background:#A8854F}
.inv-btn-2nd{background:#FFFFFF;color:#6B6356;border:1px solid #D8D0C4}
html[data-theme="dark"] .inv-btn-2nd{background:#1C1A16;border-color:#3A352B;color:#A8A095}
.inv-btn-2nd:hover{background:#F5F1E8}
.inv-btn-danger{background:#FFEAEA;color:#B71C1C;border:1px solid #FFCDD2}
.inv-btn-danger:hover{background:#FFCDD2}

.inv-status{display:inline-block;font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;text-transform:uppercase;letter-spacing:.5px}
.inv-status.st-sent{background:#E8E0D0;color:#5C4533}
.inv-status.st-viewed{background:#D6E5F0;color:#1F4F7A}
.inv-status.st-partially_paid{background:#FFE0B2;color:#8B5A2B}
.inv-status.st-paid{background:#C8E6C9;color:#1F6B1F}
.inv-status.st-overdue{background:#FFCDD2;color:#B71C1C}
.inv-status.st-draft{background:#E0E0E0;color:#666}
.inv-status.st-cancelled{background:#E0E0E0;color:#999;text-decoration:line-through}

.inv-grid{display:grid;grid-template-columns:1fr 320px;gap:14px;margin-bottom:24px}
.inv-block{background:#FFFFFF;border:1px solid #E5E0D5;border-radius:10px;padding:16px}
html[data-theme="dark"] .inv-block{background:#1C1A16;border-color:#3A352B}
.inv-block h4{margin:0 0 8px;font-size:11px;color:#8A827A;text-transform:uppercase;letter-spacing:1px;font-weight:600}
.inv-block-body{font-size:13px;line-height:1.7}
.inv-block-body .row{display:flex;justify-content:space-between;padding:3px 0}
.inv-block-body .row.total{font-weight:700;font-size:15px;border-top:1px solid #F0EBE4;padding-top:8px;margin-top:6px}
.inv-block-body .row.paid{color:#1F6B1F}
.inv-block-body .row.balance{font-weight:700;font-size:16px;color:#B71C1C;border-top:1px dashed #F0EBE4;padding-top:6px;margin-top:4px}

.inv-h3{font-size:13px;color:#8B5A2B;text-transform:uppercase;letter-spacing:1px;margin:24px 0 10px}
html[data-theme="dark"] .inv-h3{color:#BFA164}

.inv-table{width:100%;border-collapse:collapse;font-size:13px;background:#FFFFFF;border-radius:10px;overflow:hidden}
html[data-theme="dark"] .inv-table{background:#1C1A16}
.inv-table th{background:#F5F1E8;color:#6B6356;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
html[data-theme="dark"] .inv-table th{background:#252119;color:#A8A095}
.inv-table td{padding:8px 12px;border-top:1px solid #F0EBE4}
html[data-theme="dark"] .inv-table td{border-color:#2A2620}
.inv-table th.num,.inv-table td.num{text-align:right}
.cond-note{color:#C45A2C;font-weight:700;cursor:help}

.modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:2000;padding:20px}
.modal{background:#FFFFFF;width:100%;max-width:540px;border-radius:12px;padding:22px;box-shadow:0 12px 36px rgba(0,0,0,.25)}
html[data-theme="dark"] .modal{background:#1C1A16;color:#F5F1E8}
.modal h3{margin:0 0 4px}
.muted{color:#8A827A;font-size:13px;margin:0 0 12px}
.row-2{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:8px}
.field{display:flex;flex-direction:column;margin-bottom:8px}
.field label{font-size:11px;color:#6B6356;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px;font-weight:600}
.field input,.field select{padding:8px 10px;border:1px solid #D8D0C4;border-radius:6px;font-size:13px}
html[data-theme="dark"] .field input,html[data-theme="dark"] .field select{background:#0F0D0A;border-color:#3A352B;color:#F5F1E8}
.modal-foot{display:flex;justify-content:flex-end;gap:8px;margin-top:14px}

@media (max-width:768px){
  .inv-grid{grid-template-columns:1fr}
  .row-2{grid-template-columns:1fr}
  .inv-table{font-size:11px}
}
</style>
